import DashboardCard from "@/components/dashboard-card";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/atoms/card";

import { User, MapPin, Loader2 } from "lucide-react";
import OrdersTable from "@/components/orders/OrdersTable";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { Driver } from "@/types/supabase";
import { useState, useEffect, useRef, useCallback } from "react";
import { useDriversApi } from "@/hooks/useDriversApi";
import { GOOGLE_MAPS_OPTIONS } from "@/lib/googleMapsConfig";
import { formatCurrency } from "@/lib/formatCurrency";
import { EmptyState } from "@/components/atoms/empty-state";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useAuth } from "@/contexts/AuthContext";
import Pagination from "@/components/atoms/Pagination";
import { useNavigate } from "react-router-dom";
import { useOrdersStore } from "@/pages/orders-new/use-orders-store";
import { useOrder } from "@/hooks/useOrder";
import { toastHandler } from "@/lib/ToastHandler";
import StatusBadge from "@/components/orders/StatusBadge";
import { getDriverStatusText, DRIVER_STATUS } from "@/lib/statusConfig";
const isValidCoordinate = (lat?: number, lng?: number) =>
  typeof lat === "number" &&
  typeof lng === "number" &&
  lat >= -90 &&
  lat <= 90 &&
  lng >= -180 &&
  lng <= 180;

// Constante para el límite de entregas por página
// IMPORTANTE: Este valor debe mantenerse en 10 - no se deben mostrar más de 10 entregas al mismo tiempo
const DELIVERY_HISTORY_LIMIT = 10;

// Loader component
const Spinner = ({ message }: { message?: string }) => (
  <div className="flex flex-col items-center justify-center py-8 gap-2">
    <Loader2 className="animate-spin w-8 h-8 text-blue-600" />
    {message && <div className="text-gray-500 text-sm">{message || 0}</div>}
  </div>
);

export function DriverResumen({
  metrics,
  driver,
  realTimeData,
}: {
  driver: Driver;
  metrics: any;
  realTimeData?: any;
}) {
  const [deliveryHistory, setDeliveryHistory] = useState<any[]>([]);
  const [isLoadingDeliveries, setIsLoadingDeliveries] = useState(false);
  const hasFetchedRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);

  // Estado para manejar la carga inicial de datos en tiempo real
  const [isLoadingRealTimeData, setIsLoadingRealTimeData] = useState(true);
  const [hasReceivedRealTimeData, setHasReceivedRealTimeData] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  // The work state comes from the API; telemetry can arrive from an old session.
  const onDelivery = realTimeData?.driver_status === DRIVER_STATUS.ON_DELIVERY;
  const orderAccepted =
    realTimeData?.driver_status === DRIVER_STATUS.ORDER_ACCEPTED;
  const onSite = realTimeData?.driver_status === DRIVER_STATUS.ON_SITE;
  const isConnected = Boolean(driver.driver_working);

  // Refs para el mapa y marcador
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  // Use the drivers API hook
  const { fetchDriverDeliveryHistory } = useDriversApi();
  const {
    order,
    loading: orderLoading,
    error: orderError,
  } = useOrder(selectedOrderId);
  const { supabaseInfo } = useAuth();
  const navigate = useNavigate();

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Effect to handle navigation once order is loaded
  useEffect(() => {
    if (selectedOrderId && order && !orderLoading) {
      useOrdersStore.setState({ orderDetail: order });
      navigate(`/orders/${selectedOrderId}`);
      setSelectedOrderId(null);
    } else if (selectedOrderId && orderError) {
      toastHandler({
        title: "Error",
        description: "Error al cargar la orden",
        variant: "destructive",
      });
      setSelectedOrderId(null);
    }
  }, [selectedOrderId, order, orderLoading, orderError, navigate]);
  const handleOrderClick = useCallback(async (orderId: string) => {
    setSelectedOrderId(orderId);
  }, []);
  // Efecto para manejar el estado de carga de datos en tiempo real
  useEffect(() => {
    if (realTimeData !== undefined) {
      setHasReceivedRealTimeData(true);
      setIsLoadingRealTimeData(false);
    } else if (!hasReceivedRealTimeData) {
      // Si aún no hemos recibido datos y no hay realTimeData, seguimos cargando
      // Agregamos un timeout para evitar cargar indefinidamente
      const timeout = setTimeout(() => {
        setIsLoadingRealTimeData(false);
      }, 10000); // 10 segundos timeout

      return () => clearTimeout(timeout);
    }
  }, [realTimeData, hasReceivedRealTimeData]);

  const mapContainerStyle = {
    width: "100%",
    height: "400px",
  };

  const center = {
    lat: parseFloat(
      realTimeData?.lat?.toString() ||
        driver?.location_lat?.toString() ||
        "13.6776678",
    ),
    lng: parseFloat(
      realTimeData?.lng?.toString() ||
        driver?.location_lon?.toString() ||
        "-89.2944933",
    ),
  };

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_OPTIONS);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const isMobile = useMediaQuery("(max-width: 1200px)");

  // Fetch delivery history using the API hook
  const fetchOrders = async (searchValue = search, pageValue = page) => {
    setIsLoadingDeliveries(true);
    hasFetchedRef.current = driver.id;
    try {
      const isManager = supabaseInfo?.role === "manager";
      const data = isManager
        ? await fetchDriverDeliveryHistory(
            driver?.id || 0,
            {
              page: pageValue,
              limit: DELIVERY_HISTORY_LIMIT,
              search: searchValue,
            },
            supabaseInfo?.branchIds || [],
          )
        : await fetchDriverDeliveryHistory(driver?.id || 0, {
            page: pageValue,
            limit: DELIVERY_HISTORY_LIMIT,
            search: searchValue,
          });

      if (!isMountedRef.current) return;

      if (data.success && data.data) {
        const transformedDeliveries = data.data.map((delivery) => {
          const subtotal = delivery?.subtotal || 0;
          const deliveryCharge = delivery?.total_delivery_charge || 0;
          const tax = delivery?.tax || 0;
          const discount = delivery?.discount || 0;
          const total = (subtotal + deliveryCharge + tax - discount).toFixed(2);

          return {
            id: delivery?.public_id || delivery?.id?.toString() || "N/A",
            orden: `#${delivery?.id || "N/A"}`,
            orden_id: delivery?.id, // Add the numeric id for linking
            fecha: delivery?.created_at
              ? new Date(delivery.created_at).toLocaleDateString("es-ES", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                })
              : "-",
            direccion: delivery?.address?.full_address
              ? `${delivery.address.full_address}`
              : delivery?.address?.street && delivery?.address?.city
                ? `${delivery.address.street}, ${delivery.address.city}`
                : "-",
            sucursal: delivery?.branch?.name
              ? `${delivery.branch.name}`
              : delivery?.branch_id
                ? `${delivery.branch_id}`
                : "-",
            km: "N/A",
            total: total,
            status: delivery?.status || "unknown",
            currency: delivery?.currency || "USD",
            tracking_id: delivery?.tracking_id || null,
          };
        });

        // Asegurar que nunca se muestren más de DELIVERY_HISTORY_LIMIT elementos
        const limitedDeliveries = transformedDeliveries.slice(
          0,
          DELIVERY_HISTORY_LIMIT,
        );
        setDeliveryHistory(limitedDeliveries);
        setTotalCount(data.total || 0);
        setTotalPages(data.totalPages || 1);
      } else {
        setDeliveryHistory([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } catch (error) {
      if (isMountedRef.current) {
        setDeliveryHistory([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } finally {
      if (isMountedRef.current) setIsLoadingDeliveries(false);
    }
  };
  useEffect(() => {}, [realTimeData]);
  // Fetch on mount, on page change, or on search change
  useEffect(() => {
    fetchOrders(search, page);
  }, [driver.id, page, search]);

  // Nuevo useEffect para actualizar el marcador cuando cambien los datos
  useEffect(() => {
    // Remove redundant variable declarations - use component-level variables
    // iconUrl logic remains the same
    const iconUrl = isConnected
      ? onDelivery || orderAccepted || onSite
        ? "/driver-map.png"
        : "/driver-free.png"
      : "/driver-gray.png";
    // For grayscale: if not connected, grayscale(100%), else none
    const iconFilter = !isConnected ? "grayscale(100%)" : "none";
    if (mapRef.current && markerRef.current && realTimeData) {
      const newLocation = isValidCoordinate(realTimeData.lat, realTimeData.lng)
        ? { lat: realTimeData.lat, lng: realTimeData.lng }
        : center;

      // Actualizar posición del marcador
      markerRef.current.setPosition(newLocation);

      // Actualizar icono basado en el estado
      markerRef.current.setIcon({
        url: iconUrl,
        scaledSize: new window.google.maps.Size(40, 40),
        anchor: new window.google.maps.Point(20, 20),
      });

      // Aplicar filtro CSS al marcador
      if (markerRef.current.getIcon()) {
        const iconElement = markerRef.current.getIcon() as google.maps.Icon;
        markerRef.current.setIcon({
          ...iconElement,
          url: iconUrl,
          scaledSize: new window.google.maps.Size(40, 40),
          anchor: new window.google.maps.Point(20, 20),
        });

        // Aplicar estilo CSS al DOM del marcador después de un pequeño delay
        setTimeout(() => {
          const markerElement = document.querySelector(
            `img[src="${iconUrl}"]`,
          ) as HTMLImageElement;
          if (markerElement) {
            markerElement.style.filter = iconFilter;
            markerElement.style.opacity = isConnected ? "1" : "0.8";
          }
        }, 100);
      }

      // Actualizar contenido del InfoWindow
      if (infoWindowRef.current) {
        const isActiveTrip = onDelivery || orderAccepted || onSite;
        const currentStatus = realTimeData?.driver_status;

        const statusDotColor = isActiveTrip
          ? "bg-indigo-500"
          : isConnected
            ? "bg-green-500"
            : "bg-gray-500";

        const statusTextColor = isActiveTrip
          ? "text-indigo-600"
          : isConnected
            ? "text-green-600"
            : "text-gray-600";

        const statusText = getDriverStatusText(
          currentStatus,
          !!driver.driver_working,
        );

        const infoContent = `
          <div class="p-3 pt-0 max-w-xs">
            <div class="flex items-center gap-2 mb-2">
                <div class="w-3 h-3 rounded-full ${statusDotColor}"></div>
              <h3 class="font-semibold text-gray-900">${driver.name || `Conductor ${driver.id}`}</h3>
            </div>
            <div class="space-y-1 text-sm">
              <div class="flex justify-between">
                <span class="text-gray-600">Estado:</span>
                <span class="${statusTextColor} font-medium">${statusText}</span>
              </div>
              ${
                realTimeData?.tracking_id &&
                (onDelivery || orderAccepted || onSite)
                  ? `
                <div class="flex justify-between">
                  <span class="text-gray-600">Orden:</span>
                  <span class="text-gray-900 font-medium">#${realTimeData.tracking_id}</span>
                </div>
              `
                  : ""
              }
              <div class="flex justify-between">
                <span class="text-gray-600">Ubicación:</span>
                <span class="text-gray-900">${newLocation.lat.toFixed(4)}, ${newLocation.lng.toFixed(4)}</span>
              </div>
              ${
                realTimeData?.lastUpdate
                  ? `
                <div class="flex justify-between">
                  <span class="text-gray-600">Actualizado:</span>
                  <span class="text-gray-900">${new Date(realTimeData.lastUpdate).toLocaleTimeString()}</span>
                </div>
              `
                  : ""
              }
            </div>
          </div>
        `;

        infoWindowRef.current.setContent(infoContent);
      }

      // Centrar el mapa en la nueva posición
      mapRef.current.setCenter(newLocation);
    }
  }, [realTimeData, driver.name, driver.id]);

  const currentLocation =
    realTimeData && isValidCoordinate(realTimeData.lat, realTimeData.lng)
      ? { lat: realTimeData.lat, lng: realTimeData.lng }
      : center;

  if (!isLoaded) return <Spinner message="Cargando mapa..." />;
  return (
    <div className="space-y-4 mt-4 mb-8">
      {/* Performance Section */}
      <Card className="py-3 px-4 flex flex-row items-center bg-white gap-3">
        <span className="inline-flex items-center justify-center h-8 w-8 rounded-xl bg-gray-50 ring-1 ring-gray-100">
          <User className="w-5 h-5 text-gray-400" />
        </span>
        <div>
          <CardTitle className="text-base font-medium text-gray-900 mb-0">
            Rendimiento
          </CardTitle>
          <div className="text-sm text-gray-600 font-normal">
            Rendimiento, métricas e historial de órdenes
          </div>
        </div>
      </Card>

      {/* Metrics Section */}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <DashboardCard
          title="Órdenes totales"
          value={metrics?.total_orders != null ? metrics.total_orders : "-"}
        />
        <DashboardCard
          title="Órdenes completadas"
          value={
            metrics?.completed_orders != null ? metrics.completed_orders : "-"
          }
        />
        <DashboardCard
          title="Porcentaje completado"
          value={
            metrics?.completion_rate_percentage != null
              ? `${metrics.completion_rate_percentage}%`
              : "-"
          }
        />
        {/* <DashboardCard
          title="Órdenes retrasadas"
          value={metrics?.delayed_orders != null ? metrics.delayed_orders : "-"}
        />
        <DashboardCard
          title="Porcentaje retraso"
          value={
            metrics?.delayed_rate_percentage != null
              ? `${metrics.delayed_rate_percentage}%`
              : "-"
          }
        />
        <DashboardCard
          title="Promedio en ruta (min)"
          value={
            metrics?.road_average_minutes != null
              ? metrics.road_average_minutes
              : "-"
          }
        />
        <DashboardCard
          title="Recaudado"
          value={
            metrics?.amount_collected != null
              ? `$${metrics.amount_collected}`
              : "-"
          }
        /> */}
        <DashboardCard
          title="Tipo de vehículo"
          value={
            metrics?.vehicle_type
              ? metrics?.vehicle_type === "car"
                ? "Automóvil"
                : metrics?.vehicle_type === "motorcycle"
                  ? "Moto"
                  : metrics?.vehicle_type === "bicycle"
                    ? "Bicicleta"
                    : metrics?.vehicle_type
              : "-"
          }
        />
      </div>

      {/* Delivery History */}
      <Card className="bg-white">
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-medium text-gray-900">
            Historial de entregas
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({totalCount} entregas)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-4 gap-3">
            <div className="relative w-full flex flex-col md:flex-row gap-3">
              <input
                type="text"
                placeholder="Buscar por número de orden exacto"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="text-base md:text-sm border rounded-md p-3 md:p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-80 lg:w-72"
              />
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  className="w-full sm:w-auto px-4 py-3 md:py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 flex items-center justify-center gap-2 cursor-pointer transition-colors"
                  onClick={() => {
                    setSearch(searchInput);
                    setPage(1);
                  }}
                  disabled={isLoadingDeliveries || !searchInput.trim()}
                >
                  Buscar
                </button>
                {search && (
                  <button
                    className="w-full sm:w-auto px-4 py-3 md:py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-base md:text-sm cursor-pointer transition-colors"
                    onClick={() => {
                      setSearch("");
                      setSearchInput("");
                      setPage(1);
                    }}
                    disabled={isLoadingDeliveries}
                  >
                    Limpiar
                  </button>
                )}
              </div>
            </div>
          </div>
          {isLoadingDeliveries ? (
            <Spinner message="Cargando historial de entregas..." />
          ) : deliveryHistory.length === 0 ? (
            <EmptyState
              title={
                search
                  ? "No se encontraron entregas"
                  : "Este conductor no tiene órdenes registradas."
              }
              description={
                search
                  ? "Intenta con un número de orden diferente."
                  : "Cuando el conductor realice una entrega, aparecerán en este historial."
              }
            />
          ) : isMobile ? (
            <div className="flex flex-col gap-4">
              {deliveryHistory.map((delivery: any, index: number) => (
                <div
                  key={`${delivery.id}-${index}`}
                  onClick={() => handleOrderClick(delivery.orden_id)}
                  className="rounded-lg border cursor-pointer bg-white p-4 shadow-sm lg:p-6 lg:shadow-md lg:border-gray-200"
                >
                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-3 gap-2">
                    <div className="font-semibold text-gray-900 text-lg lg:text-base">
                      {delivery.tracking_id}
                    </div>
                    <StatusBadge status={delivery.status} type="order" />
                  </div>
                  <div className="text-sm text-gray-500 mb-2">
                    <span className="font-medium text-gray-700">Fecha: </span>
                    {delivery.fecha}
                  </div>
                  <div className="text-sm text-gray-500 mb-2">
                    <span className="font-medium text-gray-700">
                      Dirección:{" "}
                    </span>
                    {delivery.direccion}
                  </div>
                  <div className="text-sm text-gray-500 mb-2">
                    <span className="font-medium text-gray-700">
                      Sucursal:{" "}
                    </span>
                    {delivery.sucursal}
                  </div>
                  <div className="text-sm text-gray-900 font-semibold mt-3">
                    Total: {formatCurrency(delivery.total, delivery.currency)}
                  </div>
                </div>
              ))}
              {/* Pagination for mobile */}
              {deliveryHistory.length > 0 && (
                <div className="flex flex-col items-center mt-6 gap-3">
                  <span className="text-sm text-gray-500 text-center">
                    Mostrando página {page} de {totalPages} ({totalCount}{" "}
                    entregas)
                  </span>
                  <Pagination
                    totalPages={totalPages}
                    currentPage={page}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto w-full">
              <OrdersTable
                columns={[
                  { header: "Orden", key: "orden" },
                  { header: "Fecha", key: "fecha" },
                  { header: "Dirección de entrega", key: "direccion" },
                  { header: "Sucursal", key: "sucursal" },
                  { header: "Estado", key: "status" },
                  { header: "Total", key: "total" },
                ]}
                emptyMessage={
                  isLoadingDeliveries
                    ? "Cargando entregas..."
                    : deliveryHistory.length === 0 && search
                      ? "No se encontraron entregas que coincidan con la búsqueda."
                      : "No hay entregas para mostrar."
                }
              >
                {deliveryHistory.map((delivery: any, index: number) => (
                  <tr
                    key={`${delivery.id}-${index}`}
                    onClick={() => handleOrderClick(delivery.orden_id)}
                    className="border-b hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {delivery.tracking_id ? (
                        <div className="">
                          {delivery.tracking_id.slice(0, 20)}
                        </div>
                      ) : (
                        <div className="">{delivery.orden.slice(0, 20)}</div>
                      )}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {delivery.fecha}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {delivery.direccion && delivery.direccion.length > 20
                        ? delivery.direccion.slice(0, 20) + "..."
                        : delivery.direccion}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {delivery.sucursal}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm">
                      <StatusBadge status={delivery.status} type="order" />
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(delivery.total, delivery.currency)}
                    </td>
                  </tr>
                ))}
              </OrdersTable>
              {deliveryHistory.length > 0 && (
                <div className="flex flex-col lg:flex-row justify-between items-center mt-6 gap-3">
                  <Pagination
                    totalPages={totalPages}
                    currentPage={page}
                    onPageChange={setPage}
                  />
                  <span className="text-sm text-gray-500 text-center lg:text-left">
                    Mostrando página {page} de {totalPages}{" "}
                    {deliveryHistory.length > 0 && (
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        ({deliveryHistory.length} entregas)
                      </span>
                    )}
                  </span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Real-time Location Section */}
      <Card className="py-3 px-4 flex flex-row items-center bg-white gap-3">
        <span className="inline-flex items-center justify-center h-8 w-8 rounded-xl bg-gray-50 ring-1 ring-gray-100">
          <MapPin className="w-5 h-5 text-gray-400" />
        </span>
        <div>
          <CardTitle className="text-base font-medium text-gray-900 mb-0">
            Ubicación en tiempo real
          </CardTitle>
          <div className="text-sm text-gray-500 font-normal">
            {isLoadingRealTimeData
              ? "Conectando con el sistema de seguimiento..."
              : "Posición actual del conductor"}
          </div>
        </div>
      </Card>
      <Card className="bg-white rounded-xl overflow-hidden relative">
        <CardContent className="!p-0">
          {isLoadingRealTimeData ? (
            <div className="w-full h-[400px] flex items-center justify-center">
              <Spinner message="Conectando con el sistema de seguimiento en tiempo real..." />
            </div>
          ) : (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={currentLocation}
              zoom={15}
              options={{
                zoomControl: true,
                streetViewControl: true,
                mapTypeControl: true,
                fullscreenControl: true,
              }}
              onLoad={(map) => {
                mapRef.current = map;
                // Determinar qué imagen usar inicialmente

                const iconUrl = isConnected
                  ? onDelivery || orderAccepted || onSite
                    ? "/driver-map.png"
                    : "/driver-free.png"
                  : "/driver-gray.png";

                // Crear marcador con imagen personalizada
                const marker = new window.google.maps.Marker({
                  position: currentLocation,
                  map: map,
                  title: `Conductor ${driver?.id || "N/A"}`,
                  icon: {
                    url: iconUrl,
                    scaledSize: new window.google.maps.Size(40, 40),
                    anchor: new window.google.maps.Point(20, 20),
                  },
                });

                markerRef.current = marker;

                // Aplicar filtro CSS al marcador inicial después de un pequeño delay
                setTimeout(() => {
                  const markerElement = document.querySelector(
                    `img[src="${iconUrl}"]`,
                  ) as HTMLImageElement;
                  if (markerElement) {
                    markerElement.style.filter = isConnected
                      ? "none"
                      : "grayscale(100%)";
                    markerElement.style.opacity = isConnected ? "1" : "0.8";
                  }
                }, 100);

                // Crear InfoWindow - using same status logic as MapComponent.tsx

                // Consistent color mapping with MapComponent: active trip = indigo, connected = green, else gray
                const isActiveTrip = onDelivery || orderAccepted || onSite;
                const currentStatus = realTimeData?.driver_status;
                //const config = getDriverStatusConfig(currentStatus || (isConnected ? "available" : "offline"));

                const statusDotColor = isActiveTrip
                  ? "bg-indigo-500"
                  : isConnected
                    ? "bg-green-500"
                    : "bg-gray-500";

                const statusTextColor = isActiveTrip
                  ? "text-indigo-600"
                  : isConnected
                    ? "text-green-600"
                    : "text-gray-600";

                const statusText = getDriverStatusText(
                  currentStatus,
                  !!driver.driver_working,
                );

                const infoContent = `
                  <div class="p-3 pt-0 max-w-xs">
                    <div class="flex items-center gap-2 mb-2">
                      <div class="w-3 h-3 rounded-full ${statusDotColor}"></div>
                      <h3 class="font-semibold text-gray-900">${driver?.name || `Conductor ${driver?.id || "N/A"}`}</h3>
                    </div>
                    <div class="space-y-1 text-sm">
                      <div class="flex justify-between">
                        <span class="text-gray-600">Estado:</span>
                        <span class="${statusTextColor} font-medium">${statusText}</span>
                      </div>
                      ${
                        realTimeData?.tracking_id &&
                        (onDelivery || orderAccepted || onSite)
                          ? `
                        <div class="flex justify-between">
                          <span class="text-gray-600">Orden:</span>
                          <span class="text-gray-900 font-medium">#${realTimeData.tracking_id}</span>
                        </div>
                      `
                          : ""
                      }
                      <div class="flex justify-between">
                        <span class="text-gray-600">Ubicación:</span>
                        <span class="text-gray-900">${currentLocation?.lat?.toFixed(4) || "0.0000"}, ${currentLocation?.lng?.toFixed(4) || "0.0000"}</span>
                      </div>
                      ${
                        realTimeData?.lastUpdate
                          ? `
                        <div class="flex justify-between">
                          <span class="text-gray-600">Actualizado:</span>
                          <span class="text-gray-900">${new Date(realTimeData.lastUpdate).toLocaleTimeString()}</span>
                        </div>
                      `
                          : ""
                      }
                    </div>
                  </div>
                `;

                const infoWindow = new window.google.maps.InfoWindow({
                  content: infoContent,
                  maxWidth: 300,
                });

                infoWindowRef.current = infoWindow;

                // Agregar listener para abrir InfoWindow al hacer click en el marker
                marker.addListener("click", () => {
                  infoWindow.open(map, marker);
                });

                // Opcional: cerrar InfoWindow cuando se hace click en el mapa
                map.addListener("click", () => {
                  infoWindow.close();
                });
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
